// Global Instructions Rule Applied!
//
// Deal Execution board — the merged execution kanban (signed-off Mockup 1).
// One board across every Deal Execution pipeline: columns are the union of
// stages (shared stage library ⇒ same-named stages share a column, keyed by
// pipeline_stages.id). Cards mirror the origination format: clock age chips on
// the company row, type + origination pills, commitment per fund, and the
// current stage's checklist as tickable checkbox rows (answers audited).
// Reorder-only: cards drag within their column (pipeline_deals.sequence);
// stage moves happen on the deal form's Execution tab.
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Checkbox, Input, Select, Tag } from 'antd'
import { KanbanBoard } from '../../../core/components/KanbanBoard/index.tsx'
import ViewToolbar from '../../../core/components/ViewToolbar/index.tsx'
import { CardConfig, KanbanColumn } from '../../../core/components/KanbanBoard/interfaces.ts'
import { planColumnMove } from '../../../core/components/KanbanBoard/controller/parts/priorityUtils.ts'
import { sortItemsByPriority } from '../../../core/components/KanbanBoard/controller/parts/columnUtils.ts'
import DealAgeChips from '../../deals/components/pipelines/kanbans/early_stage/DealAgeChips.tsx'
import { daysSince, resolveBandColour } from '../../../core/utils/pipelineAge.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { PIPELINE_TYPE_DEAL_EXECUTION } from '../../../state_manager/globals.ts'
import { PipelineConfigModel } from '../../../state_manager/data_models/pipeline_config_model.ts'
import { PipelinesModel } from '../../../state_manager/data_models/pipelines_model.ts'
import { PipelineStagesModel } from '../../../state_manager/data_models/pipeline_stages_model.ts'
import {
  DealExecutionBoardRecord,
  DealExecutionBoardRecordFields as F,
  DealExecutionChecklistItem
} from '../../../state_manager/data_models/deal_execution_board_model.ts'
import { useDealExecutionBoard } from '../hooks/useDealExecutionBoard.ts'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'

const MAX_VISIBLE_CHECKS = 3

type BoardItem = DealExecutionBoardRecord & { id: string; columnId: string }

export default function DealExecutionBoard(): React.ReactElement {
  const { board, loading, filters, setFilters, refetch, reorderCard, toggleChecklistItem } = useDealExecutionBoard()
  // The shell keeps this view mounted when the user switches tabs — refetch
  // when it becomes visible again so stage moves / new enrollments made from
  // deal forms show without a manual refresh.
  const activateRef = useRefreshOnActivate(refetch)
  const { openFormInTab } = useTabManagerContext()
  const [searchDraft, setSearchDraft] = useState('')

  const pipelines = useSelector(
    (state: { globalConfigs?: { pipelines?: PipelinesModel[] } }) => state.globalConfigs?.pipelines || []
  )
  const pipelineConfigs = useSelector(
    (state: { globalConfigs?: { pipeline_configs?: PipelineConfigModel[] } }) =>
      state.globalConfigs?.pipeline_configs || []
  )
  const pipelineStages = useSelector(
    (state: { globalConfigs?: { pipeline_stages?: PipelineStagesModel[] } }) =>
      state.globalConfigs?.pipeline_stages || []
  )

  const executionPipelines = useMemo(
    () => pipelines.filter((p) => p.type === PIPELINE_TYPE_DEAL_EXECUTION),
    [pipelines]
  )

  // Union of stages across the (possibly filtered) execution pipelines,
  // keyed by the shared pipeline_stages.id so same-named stages merge.
  const columns: KanbanColumn[] = useMemo(() => {
    const pipelineIds = new Set(
      executionPipelines
        .filter((p) => !filters.pipelineId || p.id === filters.pipelineId)
        .map((p) => p.id)
    )
    const byStage = new Map<string, KanbanColumn>()
    for (const cfg of pipelineConfigs) {
      if (!pipelineIds.has(cfg.pipeline as string)) continue
      if (cfg.visible_kb === false) continue
      const stageId = cfg.stage as string
      if (byStage.has(stageId)) continue
      const stage = pipelineStages.find((s) => s.id === stageId)
      if (!stage) continue
      byStage.set(stageId, {
        id: stageId,
        name: (stage.name as string) || 'Unnamed stage',
        sequence: (stage.sequence as number | null) ?? null,
        visible: true,
        dropOption: true,
        colour: (stage.colour as string) || undefined
      })
    }
    return Array.from(byStage.values()).sort(
      (a, b) => (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER)
    )
  }, [executionPipelines, pipelineConfigs, pipelineStages, filters.pipelineId])

  // Sorted by sequence here (not only by the RPC) so an optimistic reorder
  // moves the card on screen before the next refetch.
  const items: BoardItem[] = useMemo(
    () =>
      sortItemsByPriority(
        board.map((row) => ({
          ...row,
          id: row[F.pipeline_deals__id],
          columnId: row[F.pipeline_stages__id]
        })),
        (item) => item[F.pipeline_deals__sequence] as number | null | undefined
      ),
    [board]
  )

  const handleMoveItem = async (
    itemId: string,
    fromColumnId: string,
    toColumnId: string,
    newIndex: number
  ): Promise<void> => {
    if (fromColumnId !== toColumnId) return // reorder-only; DropZone already blocks this
    const movedItem = items.find((i) => i.id === itemId)
    if (!movedItem) return
    // The column as displayed still contains the moved card and the DropZone
    // index counts it; the planner corrects for that.
    const plan = planColumnMove(
      items.filter((i) => i.columnId === toColumnId),
      movedItem,
      newIndex,
      (i) => i[F.pipeline_deals__sequence] as number | null | undefined
    )
    if (plan.sequence === null) return // dropped into its own slot
    await reorderCard(
      itemId,
      plan.sequence,
      plan.renumbered.map(({ item, sequence }) => ({ id: item.id, sequence }))
    )
  }

  const cardConfig: CardConfig<BoardItem> = useMemo(
    () => ({
      getItemId: (item) => item.id,
      getItemTitle: (item) => (item[F.deals__name] as string) || 'Unnamed deal',
      getItemTitleDisplay: (item) => (
        <span
          className='text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium'
          onClick={(e) => {
            e.stopPropagation()
            // Execution-board context: land directly on the deal form's Execution tab.
            openFormInTab('deals', item[F.deals__id], item[F.deals__name] || 'Deal', { tab: 'execution' })
          }}
        >
          {item[F.deals__name] || 'Unnamed deal'}
        </span>
      ),
      getItemSubtitle: (item) => {
        const companyId = item[F.accounts__id]
        const companyName = item[F.accounts__name]
        if (!companyName) return null
        if (!companyId) return companyName
        return (
          <span
            className='text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer'
            onClick={(e) => {
              e.stopPropagation()
              openFormInTab('companies', companyId, companyName)
            }}
          >
            {companyName}
          </span>
        )
      },
      renderItemMeta: (item) => {
        const cfg = pipelineConfigs.find((c) => c.id === item[F.pipeline_config__id])
        const pipeline = pipelines.find((p) => p.id === item[F.pipelines__id])
        if (cfg && cfg.show_age_counters === false) return null
        const daysInStage = daysSince(item[F.pipeline_deals__stage_date] as string | null)
        const daysInPipeline = daysSince(item[F.pipeline_deals__date_created] as string | null)
        return (
          <DealAgeChips
            daysInStage={daysInStage}
            stageColour={resolveBandColour(
              daysInStage,
              cfg?.stage_warn_days,
              cfg?.stage_warn_colour,
              cfg?.stage_crit_days,
              cfg?.stage_crit_colour
            )}
            daysInPipeline={daysInPipeline}
            pipelineColour={resolveBandColour(
              daysInPipeline,
              pipeline?.pipeline_warn_days,
              pipeline?.pipeline_warn_colour,
              pipeline?.pipeline_crit_days,
              pipeline?.pipeline_crit_colour
            )}
          />
        )
      },
      renderItemContent: (item) => {
        const symbol = (item[F.currencies__symbol] as string) || ''
        const commitments = (item[F.prog__commitments] || []) as Array<{
          fund_id?: string | null
          fund_name?: string | null
          amount?: number | null
        }>
        const checklist = (item[F.prog__checklist] || []) as DealExecutionChecklistItem[]
        const open = checklist.filter((c) => !c.answer)
        const done = checklist.filter((c) => c.answer)
        // Origination-card convention: completed shown ticked, open first-3 rule
        const visibleChecks = [...open, ...done]
          .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
          .slice(0, MAX_VISIBLE_CHECKS)

        return (
          <div className='space-y-1.5'>
            <div className='flex flex-wrap items-center gap-1'>
              {item[F.deals__deal_type__label] && (
                <span className='text-[11px] text-gray-400'>{item[F.deals__deal_type__label]}</span>
              )}
              {/* Origination stage only — decision status dropped from the
                  card (owner 2026-08-14). */}
              {item[F.orig__stage_name] && (
                <Tag className='text-[11px] leading-[18px] m-0 ml-auto'>
                  {item[F.orig__stage_name]}
                </Tag>
              )}
            </div>

            {commitments.length > 0 && (
              <div className='space-y-0.5'>
                {commitments.map((c, idx) => (
                  <div
                    key={c.fund_id || c.fund_name || `commitment-${idx}`}
                    className='flex text-xs tabular-nums text-gray-800'
                  >
                    <span>{c.fund_name}</span>
                    <span className='ml-auto'>
                      {symbol}
                      {c.amount != null
                        ? Number(c.amount).toLocaleString('en-US', {
                            minimumFractionDigits: Number.isInteger(Number(c.amount)) ? 0 : 2,
                            maximumFractionDigits: Number.isInteger(Number(c.amount)) ? 0 : 2
                          })
                        : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {visibleChecks.length > 0 && (
              <div className='space-y-0.5'>
                {visibleChecks.map((check) => (
                  <div key={check.id} className='flex items-center gap-1 text-xs leading-none h-4'>
                    <Checkbox
                      className='scale-75'
                      checked={check.answer}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleChecklistItem(item, check.id, e.target.checked)}
                    />
                    <span className={`truncate ${check.answer ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                      {check.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      }
    }),
    [openFormInTab, pipelineConfigs, pipelines, toggleChecklistItem]
  )

  return (
    <div ref={activateRef} className='h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4'>
      {/* Standard blue toolbar ribbon (owner spec 2026-08-12), shaped exactly
          like the other kanbans: a compact select in leftSlot (cf. the
          fundraising board's fund select), controls inline on the right —
          single row. Deal creation from this board (approval deals skipping
          origination) is deferred: add button off. */}
      <ViewToolbar
        moduleKey='deal-execution'
        searchEnabled={false}
        addButtonEnabled={false}
        onRefreshData={() => refetch()}
        leftSlot={
          <Select
            allowClear
            placeholder='Pipeline: All'
            className='min-w-[200px]'
            value={filters.pipelineId ?? undefined}
            onChange={(value) => setFilters((prev) => ({ ...prev, pipelineId: value ?? null }))}
            options={executionPipelines.map((p) => ({ value: p.id, label: p.name }))}
          />
        }
        beforeAddButtonSlot={
          <Input.Search
            placeholder='Search deals…'
            className='max-w-[220px]'
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onSearch={(value) => setFilters((prev) => ({ ...prev, search: value || null }))}
            allowClear
          />
        }
      />
      <div className='flex items-center justify-between pb-1'>
        <span className='text-xs text-gray-500'>
          {board.length} deal{board.length === 1 ? '' : 's'} in execution
        </span>
        <span className='text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5'>
          Drag to re-prioritise within a stage — stage moves happen on the deal form
        </span>
      </div>
      <div className='flex-1 min-h-0'>
        <KanbanBoard<BoardItem>
          columns={columns}
          items={items}
          cardConfig={cardConfig}
          onMoveItem={handleMoveItem}
          loading={loading}
          getColumnId={(item) => item.columnId}
          sameColumnOnly
          emptyState={{ title: 'No deals in execution' }}
        />
      </div>
    </div>
  )
}
